import { useEffect, useState } from 'react';

// material-ui
import { useTheme } from '@mui/material/styles';
import { Box, Divider, List, Typography, useMediaQuery } from '@mui/material';

// project import
import NavItem from './NavItem';
import NavGroup from './NavGroup';
import menuItem from 'menu-items';

import useConfig from 'hooks/useConfig';
import { HORIZONTAL_MAX_ITEM } from 'config';
import { useGetMenuMaster } from 'api/menu';

// types
import { NavItemType } from 'types/menu';
import { MenuOrientation } from 'types/config';
import {
  Application, Offer,
  Rights,
  Roles,
  useAccessiblePortfoliosQuery,
  useCountApplicationOfferStatsLazyQuery, useCountFundingPipelineOfferStatsLazyQuery,
  useCountTodayMcasLazyQuery
} from '../../../../../graphql/types';
import { getUserProfileFullName } from '../../../../../utils/users';
import dashboard from '../../../../../menu-items/dashboard';
import merchants from '../../../../../menu-items/merchants';
import organizations from '../../../../../menu-items/organizations';
import settings from '../../../../../menu-items/settings';
import users from '../../../../../menu-items/users';
import useAuth from '../../../../../hooks/useAuth';

import { BarChartOutlined, BlockOutlined, FolderOpenOutlined, FolderViewOutlined, FundProjectionScreenOutlined, WalletOutlined } from '@ant-design/icons';
import { useApplicationUpdates } from '../../../../../hooks/useApplicationUpdate';
import { McaEvent } from '../../../../../sockets/mcaSocketService';
import { useMcaUpdates } from '../../../../../hooks/useMcaUpdates';
import { useOfferUpdates } from "../../../../../hooks/useOfferUpdates";

// ==============================|| DRAWER CONTENT - NAVIGATION ||============================== //
const icons = {
  FolderOpenOutlined,
  FolderViewOutlined
};
const Navigation = () => {
  const theme = useTheme();
  const { user: currentUser } = useAuth();
  const { data: accessibleUsers, loading } = useAccessiblePortfoliosQuery({
    fetchPolicy: 'network-only'
  });
  const { menuOrientation } = useConfig();
  const { menuMaster } = useGetMenuMaster();
  const drawerOpen = menuMaster.isDashboardDrawerOpened;
  const downLG = useMediaQuery(theme.breakpoints.down('lg'));

  const [selectedID, setSelectedID] = useState<string | undefined>('');
  const [selectedItems, setSelectedItems] = useState<string | undefined>('');
  const [selectedLevel, setSelectedLevel] = useState<number>(0);
  const [menuItems, setMenuItems] = useState<{ items: NavItemType[] }>({ items: [] });
  const [fetchCountTodayMcas, { data: mcaData }] = useCountTodayMcasLazyQuery({
    fetchPolicy: 'network-only'
  });
  const [fetchCountOfferApplications, { data: offerData }] = useCountApplicationOfferStatsLazyQuery({
    fetchPolicy: 'network-only'
  });

  const [fetchFundingPipelineOfferStats, { data: fundingPipelineCounts }] = useCountFundingPipelineOfferStatsLazyQuery({
    fetchPolicy: 'network-only'
  });

  useMcaUpdates((event: McaEvent) => {
    if (['created', 'updated', 'deleted'].includes(event.type)) {
      fetchCountTodayMcas();
    }
  });

  useApplicationUpdates((event: { application: Application; type: 'created' | 'updated' | 'deleted' }) => {
    if (['created', 'updated', 'deleted'].includes(event.type)) {
      fetchCountOfferApplications();
      fetchFundingPipelineOfferStats();
    }
  });

  useEffect(() => {
    fetchCountTodayMcas();
    fetchCountOfferApplications();
    fetchFundingPipelineOfferStats();

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        fetchCountTodayMcas();
        fetchCountOfferApplications();
        fetchFundingPipelineOfferStats();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);
  const mcaCount = mcaData?.countTodayMCAs ?? 0;
  const offerMain = offerData?.countApplicationOfferStats?.countOfferApplications ?? 0;
  const offerSecondary = offerData?.countApplicationOfferStats?.countTodayOfferSentApplications ?? 0;
  const fundingPipelineDocsOut =
    fundingPipelineCounts?.countFundingPipelineOfferStats?.statusCounts?.find((s) => s.statusId === 'Docs Out')?.count ?? 0;
  const fundingPipelineMI =
      (fundingPipelineCounts?.countFundingPipelineOfferStats?.statusCounts ?? [])
          .filter((s) => s.statusId === 'MI' || s.statusId === 'MI - Resign')
          .reduce((sum, s) => sum + (s?.count ?? 0), 0);

  useEffect(() => {
    const extendedMerchants = merchants.children?.map((item) => {
      if (item.id === 'offers') {
        return {
          ...item,
          badges: [
            ...(item.badges ?? []),
            { count: offerMain, color: theme.palette.slateblue.main },
            { count: offerSecondary, color: theme.palette.info.dark }
          ]
        };
      }
      if (item.id === 'merchants-cash-advances') {
        return {
          ...item,
          badges: [...(item.badges ?? []), { count: mcaCount, color: theme.palette.royalblue.main }]
        };
      }
      if (item.id === 'funding-pipeline') {
        return {
          ...item,
          badges: [
            ...(item.badges ?? []),
            { count: fundingPipelineDocsOut, color: '#949494' },
            { count: fundingPipelineMI, color: '#f28a9c' }
          ]
        };
      }

      return item;
    });
    if (accessibleUsers?.accessiblePortfolios.length) {
      const portfolioMenuItems = accessibleUsers?.accessiblePortfolios.map((user) => {
        const investorRole = user.rights?.includes(Rights.IsFunder) ? Roles.Funder : Roles.Investor;
        let subMenus: any = [];
        subMenus.unshift(
          {
            id: 'dashboard' + user.id,
            title: investorRole === Roles.Funder ? 'Funder Dashboard' : 'Investor Dashboard',
            type: 'item',
            url: `/dashboard/${investorRole === Roles.Funder ? 'funder' : 'investor'}/${user.id}`,
            icon: { anticon: FundProjectionScreenOutlined }
          },
          {
            id: 'investor-ledger',
            title: 'Ledger',
            type: 'item',
            url: '/ledger/' + user.id,
            icon: { anticon: WalletOutlined }
          },
          {
            id: 'portfolio' + user.id,
            title: 'My Portfolio',
            type: 'item',
            url: '/portfolio/' + user.id,
            icon: { anticon: icons.FolderOpenOutlined }
          }
        );
        return {
          id: 'investor-portfolio-' + user.id,
          title: getUserProfileFullName(user),
          type: 'collapse',
          url: '/portfolio/' + user.id,
          avatarForUser: user,
          children: subMenus
        };
      });

      if (portfolioMenuItems?.length > 1) {
        portfolioMenuItems.unshift(
          {
            id: 'investor-portfolios-dashboard',
            title: 'Multi-Dashboard',
            type: 'item',
            url: '/dashboard/portfolios',
            icon: { anticon: BarChartOutlined }
          },
          {
            id: 'investor-portfolios',
            title: 'Multi-Portfolios',
            type: 'item',
            url: '/portfolios',
            icon: { anticon: BlockOutlined }
          }
        );
      }

      setMenuItems({
        items: [
          dashboard,
          {
            ...merchants,
            children: extendedMerchants
          },
          {
            id: 'portfolios',
            title: 'Portfolios',
            type: 'group',
            children: portfolioMenuItems
          },
          organizations,
          settings,
          users
        ]
      });
    } else {
      setMenuItems({
        items: [
          dashboard,
          {
            ...merchants,
            children: extendedMerchants
          },
          organizations,
          settings,
          users
        ]
      });
    }
    // eslint-disable-next-line
  }, [loading, offerData, mcaData, fundingPipelineCounts]);

  const isHorizontal = menuOrientation === MenuOrientation.HORIZONTAL && !downLG;

  const lastItem = isHorizontal ? HORIZONTAL_MAX_ITEM : null;
  let lastItemIndex = menuItems.items.length - 1;
  let remItems: NavItemType[] = [];
  let lastItemId: string;

  //  first it checks menu item is more than giving HORIZONTAL_MAX_ITEM after that get lastItemid by giving horizontal max
  // item and it sets horizontal menu by giving horizontal max item lastly slice menuItem from array and set into remItems

  if (lastItem && lastItem < menuItems.items.length) {
    lastItemId = menuItems.items[lastItem - 1].id!;
    lastItemIndex = lastItem - 1;
    remItems = menuItems.items.slice(lastItem - 1, menuItems.items.length).map((item) => ({
      title: item.title,
      elements: item.children,
      icon: { anticon: item.icon },
      ...(item.url && {
        url: item.url
      })
    }));
  }

  const navGroups = menuItems.items.slice(0, lastItemIndex + 1).map((item, index) => {
    if (item.rights && !item.rights.every((x) => currentUser?.rights.includes(x))) {
      return null;
    }
    if (item.oneOfRights && !item.oneOfRights.filter((x) => currentUser?.rights.includes(x)).length) {
      return null;
    }
    switch (item.type) {
      case 'group':
        if (item.url && item.id !== lastItemId) {
          return (
            <List key={item.id} {...(isHorizontal && { sx: { mt: 0.5 } })}>
              {!isHorizontal && index !== 0 && <Divider sx={{ my: 0.5 }} />}
              <NavItem item={item} level={1} isParents />
            </List>
          );
        }

        return (
          <NavGroup
            key={item.id}
            setSelectedID={setSelectedID}
            setSelectedItems={setSelectedItems}
            setSelectedLevel={setSelectedLevel}
            selectedLevel={selectedLevel}
            selectedID={selectedID}
            selectedItems={selectedItems}
            lastItem={lastItem!}
            remItems={remItems}
            lastItemId={lastItemId}
            item={item}
          />
        );
      default:
        return (
          <Typography key={item.id} variant="h6" color="error" align="center">
            Fix - Navigation Group
          </Typography>
        );
    }
  });

  return (
    <Box
      sx={{
        pt: drawerOpen ? (isHorizontal ? 0 : 2) : 0,
        ...(!isHorizontal && {
          '& > ul:first-of-type': { mt: 0 }
        }),
        display: isHorizontal ? { xs: 'block', lg: 'flex' } : 'block'
      }}
    >
      {navGroups}
    </Box>
  );
};

export default Navigation;
